import type { User as UserType } from "features/users/types";
import { memo, useCallback, useEffect } from "react";
import ResourceLayout from 'ui/layouts/Resource';
import Resource from "ui/widgets/Resource";
import type { GridRowIdGetter } from "@mui/x-data-grid";
import useUsers from "./useUsers";
import { TableGridMode } from "consts/table";
import { FilterVariant } from "consts/filters";
import { Filters, multi, text } from "consts/users";
import {
    selectIsUsersLoading,
    selectIsUsersUninitialized,
    selectRoleOptions,
    selectUserSlice
} from "features/users/selectors";
import useActions from "features/users/useActions";
import { useTypedSelector } from "hooks";
import UsersToolbar from "./UsersToolbar";
import UsersCreateEditModal from "./UsersCreateEditModal";
import { NoDataOverlay } from "ui/molecules/TableGrids";
import Tabs from "ui/atoms/Tabs";
import RolesPermissions from "./RolesPermissions";

const filterTypes = new Map<FilterVariant, string[]>([
    [FilterVariant.MultiOption, multi],
    [FilterVariant.Text, text]
]);

const User = () => {
    const getRowId: GridRowIdGetter<UserType> = useCallback(({ coreId }: UserType) =>
        coreId, []);

    const { getRoles } = useActions();
    const roleOptions = useTypedSelector(selectRoleOptions);

    useEffect(() => {
        getRoles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <ResourceLayout
            title='Merchants | Users'
            breadcrumbsProps={{
                heading: 'Users',
                links: [{ name: '' }],
                sx: {
                    ml: 4,
                    mb: 0,
                    '& + div > div': {
                        mt: 0
                    }
                }
            }}
        >
            <Tabs
                TabListContainerProps={{
                    sx: {
                        px: 4,
                        borderBottom: 1,
                        borderColor: 'divider'
                    }
                }}
                TabPanelProps={{
                    sx: {
                        px: 0
                    }
                }}
                model={[
                    {
                        label: 'Users',
                        key: 'users',
                        component: (
                            <Resource
                                useResource={useUsers}
                                mode={TableGridMode.User}
                                selectIsTableUninitialized={selectIsUsersUninitialized}
                                selectIsTableLoading={selectIsUsersLoading}
                                selectTableDataSlice={selectUserSlice}
                                getRowId={getRowId}
                                filterTypes={filterTypes}
                                FiltersProps={{
                                    filtersModelOverrides: {
                                        [Filters.roleId]: roleOptions
                                    }
                                }}
                                Toolbar={UsersToolbar}
                                NoRowsOverlay={() => (
                                    <NoDataOverlay
                                        DataEntityName='users'
                                    />
                                )}
                                Modal={UsersCreateEditModal}
                            />
                        )
                    },
                    {
                        label: 'Roles & Permissions',
                        key: 'rolesPermissions',
                        component: <RolesPermissions />
                    }
                ]}
            />
        </ResourceLayout>
    );
};

export default memo(User);
